import * as React from 'react';
import { t } from '@reos/i18n';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { DetailTimeline, EmptyState, type TimelineItem } from '@redhand/ui';
import { useSession } from '../stores/session.js';
import { PropertyGallery } from '../components/PropertyGallery.js';
import { PropertyAddressFix } from '../components/property-address-fix.js';
import { MatchesPanel } from '../components/matches-panel.js';
import { PropertyViewingsPanel } from '../components/property-viewings-panel.js';
import {
  errorMessage,
  factMoney,
  fetchPropertyDetail,
  fetchPropertyDocuments,
  fetchPropertyFacts,
  fetchPropertyCovers,
  mediaSrc,
  fetchPropertyWorkflow,
  formatDate,
  formatDateTime,
  formatMoney,
  kindLabel,
  stageLabel,
  LISTING_STAGES,
  type PropertyFact,
} from '../lib/properties.js';

/** Prodejni proces: hotove/aktualni kroky znaci zlata REDREAL (dekorativne). */
function StageSteps({ stage }: { readonly stage: string | null }): React.JSX.Element {
  const currentIndex = stage === null ? -1 : (LISTING_STAGES as readonly string[]).indexOf(stage);
  if (stage !== null && currentIndex === -1) {
    return (
      <p className="text-sm">{t('webAdmin.propertyDetail.processStatus', { locale: 'cs', values: { stage: stageLabel(stage) } })}</p>
    );
  }
  if (stage === null) {
    return (
      <EmptyState
        title={t('webAdmin.propertyDetail.processNotRunningTitle', { locale: 'cs' })}
        description={t('webAdmin.propertyDetail.processNotRunningDescription', { locale: 'cs' })}
      />
    );
  }
  return (
    <ol className="stage-steps" aria-label={t('webAdmin.propertyDetail.processAriaLabel', { locale: 'cs' })}>
      {LISTING_STAGES.map((s, index) => {
        const state = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo';
        return (
          <li
            key={s}
            className={`stage-steps__item${state !== 'todo' ? ` stage-steps__item--${state}` : ''}`}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="stage-steps__bar" aria-hidden="true" />
            <span className="stage-steps__dot" aria-hidden="true" />
            {stageLabel(s)}
          </li>
        );
      })}
    </ol>
  );
}

function factNumber(facts: readonly PropertyFact[] | undefined, key: string): string | null {
  const fact = facts?.find((f) => f.factKey === key);
  if (fact === undefined) return null;
  if (typeof fact.value === 'number' || typeof fact.value === 'string') {
    return `${fact.value}${fact.unit === 'm2' ? ' m²' : fact.unit !== null ? ` ${fact.unit}` : ''}`;
  }
  return null;
}

function Section({ title, children }: { readonly title: string; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <section className="rh-card">
      <h2 className="text-base font-semibold mb-3">
        <span className="gold-kicker" aria-hidden="true" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function QueryState({
  loading,
  error,
  loadingText,
}: {
  readonly loading: boolean;
  readonly error: unknown;
  readonly loadingText: string;
}): React.JSX.Element | null {
  if (loading) return <p role="status">{loadingText}</p>;
  if (error !== null && error !== undefined) {
    return (
      <p role="alert" className="text-sm" style={{ color: 'var(--rh-danger)' }}>
        {errorMessage(error)}
      </p>
    );
  }
  return null;
}

export function NemovitostDetailPage(): React.JSX.Element {
  const user = useSession((s) => s.user);
  const params = useParams({ strict: false }) as { readonly id?: string };
  const id = params.id;

  const detail = useQuery({
    queryKey: ['properties', id],
    queryFn: () => fetchPropertyDetail(id ?? ''),
    enabled: user !== null && id !== undefined,
  });

  const workflow = useQuery({
    queryKey: ['properties', id, 'workflow'],
    queryFn: () => fetchPropertyWorkflow(id ?? ''),
    enabled: user !== null && id !== undefined,
  });

  const facts = useQuery({
    queryKey: ['properties', id, 'facts'],
    queryFn: () => fetchPropertyFacts(id ?? ''),
    enabled: user !== null && id !== undefined,
  });

  const cover = useQuery({
    queryKey: ['properties', id, 'cover'],
    queryFn: () => fetchPropertyCovers([id ?? '']),
    enabled: user !== null && id !== undefined,
  });

  const documents = useQuery({
    queryKey: ['properties', id, 'documents'],
    queryFn: () => fetchPropertyDocuments(id ?? ''),
    enabled: user !== null && id !== undefined,
  });

  if (!user) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">{t('webAdmin.propertyDetail.heading', { locale: 'cs' })}</h1>
        <p>
          {t('webAdmin.propertyDetail.loginPromptPrefix', { locale: 'cs' })}{' '}
          <Link to="/login" className="underline">
            {t('webAdmin.propertyDetail.loginLinkText', { locale: 'cs' })}
          </Link>
          .
        </p>
      </div>
    );
  }

  if (id === undefined || detail.isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">{t('webAdmin.propertyDetail.heading', { locale: 'cs' })}</h1>
        <p role="status">{t('webAdmin.propertyDetail.loading', { locale: 'cs' })}</p>
      </div>
    );
  }

  if (detail.error !== null && detail.error !== undefined) {
    return (
      /*
       * Konvence `*-load-error` (stejne `case-load-error`, `calendar-load-error`,
       * `task-load-error`, `demand-load-error`): obrazovka, ktera se NENACETLA.
       * Regresni sada proti preview podle ni pozna vlastni zahlceni (429) a
       * navigaci zopakuje misto cekani do timeoutu — viz `pockejNaCil`
       * v `test/e2e-preview/helpers.ts`.
       */
      <div className="space-y-4" data-testid="property-load-error">
        <h1 className="text-2xl font-bold">{t('webAdmin.propertyDetail.heading', { locale: 'cs' })}</h1>
        <EmptyState
          title={t('webAdmin.propertyDetail.loadErrorTitle', { locale: 'cs' })}
          description={errorMessage(detail.error)}
          action={
            <span className="flex gap-2">
              <button type="button" className="rh-btn" onClick={() => void detail.refetch()}>
                {t('webAdmin.propertyDetail.retry', { locale: 'cs' })}
              </button>
              <Link to="/nemovitosti" className="rh-btn">
                {t('webAdmin.propertyDetail.backToList', { locale: 'cs' })}
              </Link>
            </span>
          }
        />
      </div>
    );
  }

  const property = detail.data;
  if (property === undefined) return <React.Fragment />;

  const price = formatMoney(factMoney(facts.data, 'asking_price'));
  const area = factNumber(facts.data, 'living_area_m2') ?? factNumber(facts.data, 'land_area_m2');
  const energyClass = factNumber(facts.data, 'energy_class');
  const addressLines =
    property.address !== null && property.address.lines.length > 0
      ? property.address.lines
      : property.address !== null
        ? [property.address.formatted]
        : [];
  // Titulni fotka do hlavicky (krok O6): jeden davkovy dotaz, stejny endpoint
  // jako seznamy - detail nemusi resit, jestli jde o stream nebo presign.
  const coverThumbUrl = cover.data?.[0]?.thumbUrl;
  const coverUrl = coverThumbUrl === undefined || coverThumbUrl === null ? null : mediaSrc(coverThumbUrl);
  const stage = workflow.data?.stage ?? null;

  const historyItems: readonly TimelineItem[] = [...(workflow.data?.transitions ?? [])]
    .reverse()
    .map(
      (transition, index): TimelineItem => ({
        id: `${transition.occurredAt}-${index}`,
        time: formatDateTime(transition.occurredAt),
        title:
          transition.fromState === null
            ? t('webAdmin.propertyDetail.processStartedPrefix', { locale: 'cs', values: { stage: stageLabel(transition.toState) } })
            : `${stageLabel(transition.fromState)} → ${stageLabel(transition.toState)}`,
        kind: 'status',
      }),
    );

  return (
    <div className="space-y-4">
      <nav aria-label={t('webAdmin.propertyDetail.breadcrumbAriaLabel', { locale: 'cs' })} className="text-sm">
        <Link to="/nemovitosti" className="underline">
          {t('webAdmin.propertyDetail.backToProperties', { locale: 'cs' })}
        </Link>
      </nav>

      <header className="rh-card">
        <div className="flex flex-col md:flex-row gap-4">
          {coverUrl !== null ? (
            <img
              src={coverUrl}
              crossOrigin="use-credentials"
              alt={t('webAdmin.propertyDetail.coverPhotoAlt', { locale: 'cs' })}
              className="w-full md:w-56 h-40 object-cover rounded-lg shrink-0"
            />
          ) : null}
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">
              {property.name ?? property.address?.formatted ?? t('webAdmin.propertyDetail.unnamed', { locale: 'cs' })}
            </h1>
            {addressLines.map((line) => (
              <p key={line} className="text-sm" style={{ color: 'var(--rh-text-muted)' }} data-testid="property-detail-address">
                {line}
              </p>
            ))}
            {/*
             * Krok PROP7B bod 2 — oprava adresy prímo u adresy, kterou karta
             * ukazuje. Po uspechu si detail nacte znovu, takze hlavicka ukaze
             * NOVOU adresu; upozorneni na vystupy se starou adresou kresli
             * komponenta z odpovedi API.
             */}
            <PropertyAddressFix
              propertyId={id}
              currentAddress={property.address?.formatted ?? null}
              onChanged={() => {
                void detail.refetch();
              }}
            />
            <p className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rh-badge rh-badge--neutral">{kindLabel(property.kind)}</span>
              <span className="rh-badge rh-badge--neutral">{stageLabel(stage)}</span>
              {property.status === 'draft' ? (
                <span className="rh-badge rh-badge--warning">{t('webAdmin.propertyDetail.draftBadge', { locale: 'cs' })}</span>
              ) : null}
              <span className="text-lg font-semibold">{price ?? t('webAdmin.propertyDetail.priceNotSet', { locale: 'cs' })}</span>
            </p>
            <p className="mt-1 text-sm" style={{ color: 'var(--rh-text-muted)' }}>
              {area !== null ? t('webAdmin.propertyDetail.areaPrefix', { locale: 'cs', values: { area } }) : ''}
              {area !== null && energyClass !== null ? ' · ' : ''}
              {energyClass !== null
                ? t('webAdmin.propertyDetail.energyClassPrefix', { locale: 'cs', values: { energyClass } })
                : ''}
            </p>
            {/*
              Krok P8F1 bod 3: karta bez parcely. Druhy byt v jednom dome ji
              nedostal — parcelu drzi karta prvniho bytu a unikat se nemeni.
              Prověření takovou kartu dobehne (z katastru jen nic nevytezi),
              takze tohle je UPOZORNENI S CESTOU, ne chyba. `parcelCount`
              muze chybet (cesta, ktera ho necte) — pak netvrdime nic.
            */}
            {property.parcelCount === 0 ? (
              <p className="mt-2 text-sm" role="status" data-testid="property-detail-no-parcel" style={{ color: 'var(--rh-warning-strong)' }}>
                {t('webAdmin.propertyDetail.noParcel', { locale: 'cs' })}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-4">
          <QueryState
            loading={workflow.isLoading}
            error={workflow.error}
            loadingText={t('webAdmin.propertyDetail.loadingProcessStatus', { locale: 'cs' })}
          />
          {!workflow.isLoading && (workflow.error === null || workflow.error === undefined) ? (
            <StageSteps stage={stage} />
          ) : null}
        </div>
      </header>

      {/* Krok CRM4: vhodné poptávky (párování) a prohlídky přes odkaz. */}
      <Section title={t('webAdmin.matches.propertyHeading', { locale: 'cs' })}>
        <MatchesPanel propertyId={id} />
      </Section>

      <Section title={t('webAdmin.viewings.heading', { locale: 'cs' })}>
        <PropertyViewingsPanel propertyId={id} />
      </Section>

      <Section title={t('webAdmin.gallery.sectionTitle', { locale: 'cs' })}>
        <PropertyGallery propertyId={id} />
      </Section>

      <Section title={t('webAdmin.propertyDetail.documentsSectionTitle', { locale: 'cs' })}>
        <QueryState
          loading={documents.isLoading}
          error={documents.error}
          loadingText={t('webAdmin.propertyDetail.loadingDocuments', { locale: 'cs' })}
        />
        {documents.data !== undefined ? (
          documents.data.length > 0 ? (
            <ul className="m-0 p-0 list-none">
              {documents.data.map((d) => (
                <li
                  key={d.documentId}
                  className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 py-2"
                  style={{ borderColor: 'var(--rh-border)' }}
                >
                  <span className="font-medium">{d.name}</span>
                  <span className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
                    <span className="rh-badge rh-badge--neutral">{d.kind}</span> {formatDate(d.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title={t('webAdmin.propertyDetail.noDocumentsTitle', { locale: 'cs' })}
              description={t('webAdmin.propertyDetail.noDocumentsDescription', { locale: 'cs' })}
            />
          )
        ) : null}
      </Section>

      <Section title={t('webAdmin.propertyDetail.historySectionTitle', { locale: 'cs' })}>
        <QueryState
          loading={workflow.isLoading}
          error={workflow.error}
          loadingText={t('webAdmin.propertyDetail.loadingHistory', { locale: 'cs' })}
        />
        {workflow.data !== undefined ? (
          <DetailTimeline
            ariaLabel={t('webAdmin.propertyDetail.historyAriaLabel', { locale: 'cs' })}
            items={historyItems}
            emptyState={
              <EmptyState
                title={t('webAdmin.propertyDetail.noHistoryTitle', { locale: 'cs' })}
                description={t('webAdmin.propertyDetail.noHistoryDescription', { locale: 'cs' })}
              />
            }
          />
        ) : null}
      </Section>
    </div>
  );
}
